type TypographyTheme = "light" | "dark";

type TypographyToken = {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
  color: string;
};

type TypographyScale = "display" | "headline" | "body" | "caption";

const baseTokens: Record<TypographyScale, Omit<TypographyToken, "fontFamily" | "color">> = {
  display:   { fontWeight: 700, fontSize: "2.5rem", lineHeight: "3rem", letterSpacing: "-0.02em" },
  headline:  { fontWeight: 600, fontSize: "1.5rem", lineHeight: "2rem", letterSpacing: "-0.01em" },
  body:      { fontWeight: 400, fontSize: "1rem",   lineHeight: "1.5rem", letterSpacing: "0em" },
  caption:   { fontWeight: 400, fontSize: "0.875rem", lineHeight: "1.25rem", letterSpacing: "0.01em" },
};

// Utilitário para calcular luminância relativa
function luminance(hex: string) {
  const a = hex.replace("#", "").match(/.{2}/g)?.map(x => parseInt(x, 16) / 255) ?? [0,0,0];
  const f = (c: number) => c <= 0.03928 ? c/12.92 : Math.pow((c+0.055)/1.055, 2.4);
  return 0.2126 * f(a[0]) + 0.7152 * f(a[1]) + 0.0722 * f(a[2]);
}

// Contraste conforme WCAG
function contrastRatio(hex1: string, hex2: string) {
  const l1 = luminance(hex1);
  const l2 = luminance(hex2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

// Escolhe cor acessível para fundo claro/escuro
function getAccessibleColor(theme: TypographyTheme, minContrast = 4.5, bgLight = "#fff", bgDark = "#222") {
  // Cores sugeridas
  const candidates = theme === "dark"
    ? ["#fff", "#f5f5f5", "#eaeaea", "#ffd700"]
    : ["#222", "#111", "#000", "#0057b8"];
  const bg = theme === "dark" ? bgDark : bgLight;
  return candidates.find(c => contrastRatio(c, bg) >= minContrast) || (theme === "dark" ? "#fff" : "#222");
}


export function getTypographyTokens(
  fontFamily: string,
  opts?: { theme?: TypographyTheme }
): Record<TypographyScale, TypographyToken> {
  const theme = opts?.theme ?? "light";
  // Usa cor acessível automática
  const color = getAccessibleColor(theme);

  return Object.fromEntries(
    Object.entries(baseTokens).map(([scale, token]) => [
      scale,
      {
        fontFamily: `'${fontFamily}', sans-serif`,
        color,
        ...token,
      }
    ])
  ) as Record<TypographyScale, TypographyToken>;
}

// Example typographyConfig definition (replace with your actual config or import if defined elsewhere)
const typographyConfig = {
  display: {
    family: "Arial",
    weight: 700,
    size: "2.5rem",
    lineHeight: "3rem",
    letterSpacing: "-0.02em"
  },
  headline: {
    family: "Arial",
    weight: 600,
    size: "1.5rem",
    lineHeight: "2rem",
    letterSpacing: "-0.01em"
  },
  body: {
    family: "Arial",
    weight: 400,
    size: "1rem",
    lineHeight: "1.5rem",
    letterSpacing: "0em"
  },
  caption: {
    family: "Arial",
    weight: 400,
    size: "0.875rem",
    lineHeight: "1.25rem",
    letterSpacing: "0.01em"
  }
};

export function getDeclarativeTypographyToken(
  config: typeof typographyConfig,
  key: keyof typeof typographyConfig,
  color?: string,
  theme: TypographyTheme = "light"
) {
  const t = config[key];
  // Se não passar cor, usa automática
  const resolvedColor = color ?? getAccessibleColor(theme);
  return {
    fontFamily: `'${t.family}', sans-serif`,
    fontWeight: t.weight,
    fontSize: t.size,
    lineHeight: t.lineHeight,
    letterSpacing: t.letterSpacing,
    color: resolvedColor,
  };
}

// Hook para consumir tokens tipográficos no React
import { useMemo } from "react";

export function useTypographyTokens(
  fontFamily: string,
  opts?: { theme?: TypographyTheme }
): Record<TypographyScale, TypographyToken> {
  return useMemo(() => getTypographyTokens(fontFamily, opts), [fontFamily, opts]);
}

// Helper para obter apenas um token de escala específica
export function getTypographyToken(
  fontFamily: string,
  scale: TypographyScale,
  opts?: { theme?: TypographyTheme }
): TypographyToken {
  return getTypographyTokens(fontFamily, opts)[scale];
}